import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { translations } from "@/i18n/translations";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, object=auth
  const [language, setLanguageState] = useState(localStorage.getItem("cms_lang") || "en");
  const [theme, setThemeState] = useState(localStorage.getItem("cms_theme") || "light");
  const [activeBranchId, setActiveBranchIdState] = useState(localStorage.getItem("cms_branch") || null);

  // theme apply
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("cms_theme", theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem("cms_lang", language); }, [language]);
  useEffect(() => {
    if (activeBranchId) localStorage.setItem("cms_branch", activeBranchId);
    else localStorage.removeItem("cms_branch");
  }, [activeBranchId]);

  const t = useCallback((key) => translations[language]?.[key] ?? translations.en[key] ?? key, [language]);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("cms_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.language) setLanguageState(data.language);
      if (data.theme) setThemeState(data.theme);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password, remember) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    localStorage.setItem("cms_token", data.token);
    setUser(data.user);
    if (data.user.language) setLanguageState(data.user.language);
    if (data.user.theme) setThemeState(data.user.theme);
    // default branch context
    if (!data.user.is_super_admin && data.user.branches?.length === 1) {
      setActiveBranchIdState(data.user.branches[0].id);
    }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cms_token");
    localStorage.removeItem("cms_branch");
    setActiveBranchIdState(null);
    setUser(false);
  };

  const setLanguage = async (lang) => {
    setLanguageState(lang);
    if (user) { try { await api.put("/profile", { language: lang }); } catch {} }
  };
  const setTheme = async (th) => {
    setThemeState(th);
    if (user) { try { await api.put("/profile", { theme: th }); } catch {} }
  };
  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const can = useCallback((perm, branchId = null) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    if (!user.permissions?.includes(perm)) return false;
    if (branchId && !user.branch_ids?.includes(branchId)) return false;
    return true;
  }, [user]);

  const activeBranch = user?.branches?.find((b) => b.id === activeBranchId) || null;
  const isOrgView = user?.is_super_admin && !activeBranchId;

  const value = {
    user, setUser, language, setLanguage, theme, setTheme, toggleTheme, t,
    login, logout, loadMe, can, apiError,
    activeBranchId, setActiveBranchId: setActiveBranchIdState, activeBranch, isOrgView,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
